// Shared verbatim with portal/src/utils/metaTrackingCore.js. No framework dependency.
// Meta runs on the marketing origin, never in the authenticated portal document.
const ATTRIBUTION = 'ub_meta_attribution';
const LEDGER = 'ub_meta_events_v1';
const DAYS = 86400000;
const EVENTS = new Set(['PageView', 'ViewContent', 'WixInstallClick', 'PortalOpen', 'CompleteRegistration', 'InitiateCheckout', 'StartTrial']);

export function createMetaTracking({ window: win, pixelId, frameUrl, surface, now = Date.now }) {
  const validId = /^\d{5,20}$/.test(pixelId || '');
  let frame, ready = false, lastPage = '', queue = [];
  let ledger = {};
  const host = win?.location.hostname;
  const production = host === 'ultimo-bots.com' || host === 'www.ultimo-bots.com' || host === 'portal.ultimo-bots.com';
  // Local/preview builds may exercise the UI, but never send real Meta traffic.
  const enabled = validId && production;
  const domain = production ? '; Domain=ultimo-bots.com' : '';
  const secure = win?.location.protocol === 'https:' ? '; Secure' : '';
  const origin = new URL(frameUrl || 'https://ultimo-bots.com/meta-pixel-v2.html').origin;
  const measurable = () => enabled && !new URLSearchParams(win.location.search).has('demo');
  function readCookie(key) {
    try { return decodeURIComponent(win.document.cookie.split('; ').find(v => v.startsWith(key + '='))?.slice(key.length + 1) || ''); } catch { return ''; }
  }
  function cookie(key, value, age) {
    try { win.document.cookie = `${key}=${encodeURIComponent(value)}; Path=/; Max-Age=${age}${domain}${secure}; SameSite=Lax`; } catch { /* blocked iframe storage */ }
  }
  function uuid() { return win.crypto.randomUUID(); }
  function saveLedger() {
    const entries = Object.entries(ledger).filter(([, v]) => now() - v.time < 90 * DAYS).slice(-200);
    ledger = Object.fromEntries(entries);
    try { win.localStorage.setItem(LEDGER, JSON.stringify(ledger)); } catch { /* memory dedup still works */ }
  }
  function captureAttribution() {
    if (!measurable()) return;
    const params = new URLSearchParams(win.location.search);
    const fbclid = params.get('fbclid');
    if (fbclid && /^[a-zA-Z0-9_-]{5,500}$/.test(fbclid)) {
      // Preserve the original click timestamp when re-rendering the landing page.
      const existing = readCookie('_fbc');
      if (!existing.endsWith('.' + fbclid)) cookie('_fbc', `fb.1.${now()}.${fbclid}`, 90 * 86400);
    }
    const attribution = {};
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const value = params.get(key);
      if (value && /^[a-zA-Z0-9_.|+ -]{1,200}$/.test(value)) attribution[key] = value;
    }
    if (fbclid || attribution.utm_source) cookie(ATTRIBUTION, JSON.stringify({ ...attribution, ts: now() }), 90 * 86400);
  }
  function post(message) { frame?.contentWindow?.postMessage(message, origin); }
  function flush() {
    if (!ready || !measurable()) return;
    // Automatic measurement policy, not a claim of user consent.
    post({ type: 'ub-meta-init', pixelId });
    for (const event of queue) post({ type: 'ub-meta-event', ...event });
  }
  function ensureFrame() {
    if (!measurable() || frame) return;
    frame = win.document.createElement('iframe');
    frame.src = frameUrl;
    frame.title = 'Meta measurement';
    frame.hidden = true;
    frame.setAttribute('aria-hidden', 'true');
    frame.referrerPolicy = 'no-referrer';
    // No portal DOM, auth storage, query or referrer is exposed across this origin boundary.
    win.document.body.appendChild(frame);
  }
  function track(name, data = {}, onceKey) {
    try {
      if (!measurable() || !EVENTS.has(name)) return false;
      // Re-read for cross-tab dedup. The stable eventID also helps Meta deduplicate retries.
      try { ledger = { ...ledger, ...JSON.parse(win.localStorage.getItem(LEDGER) || '{}') }; } catch { /* memory fallback */ }
      const key = onceKey && `${pixelId}:${name}:${onceKey}`;
      const existing = key && ledger[key];
      if (existing && now() - existing.time < 90 * DAYS) return false;
      const eventID = uuid();
      const safeData = { surface };
      // No arbitrary objects, user IDs, email, bot names, URL queries or currency guesses.
      for (const field of ['content_name', 'content_category']) {
        if (typeof data[field] === 'string' && /^[a-zA-Z0-9_./ -]{1,80}$/.test(data[field])) safeData[field] = data[field];
      }
      const event = { name, data: safeData, eventID };
      // Retain a small outbox for conversions until the isolated SDK accepts them.
      queue.push(event);
      if (queue.length > 200) queue.shift();
      if (key) { ledger[key] = { time: now(), eventID, pending: event }; saveLedger(); }
      captureAttribution(); ensureFrame(); flush();
      return true;
    } catch { return false; } // measurement must never block product use
  }
  function page(path) {
    if (!measurable()) return;
    // Portal routes are categories, never paths with resource IDs. Website paths are public.
    const clean = surface === 'portal'
      ? (['/wix-start', '/register', '/login'].includes(path) ? path : '/portal')
      : String(path).split(/[?#]/)[0].slice(0, 80);
    if (lastPage === clean) return;
    if (track('PageView', { content_name: clean })) {
      lastPage = clean;
      if (/\/(?:de\/)?apps\/wix$/.test(clean)) track('ViewContent', { content_name: 'Wix AI Agent', content_category: 'wix' });
    }
  }
  function start() {
    if (!win || !measurable()) return () => {};
    const message = event => {
      if (event.origin !== origin || event.source !== frame?.contentWindow) return;
      if (event.data?.type === 'ub-meta-ready') { ready = true; flush(); }
      if (event.data?.type === 'ub-meta-accepted' && measurable()) {
        const eventID = event.data.eventID;
        queue = queue.filter(item => item.eventID !== eventID);
        for (const value of Object.values(ledger)) {
          if (value.eventID === eventID) delete value.pending;
        }
        saveLedger();
      }
    };
    win.addEventListener('message', message);
    try {
      ledger = JSON.parse(win.localStorage.getItem(LEDGER) || '{}');
      for (const value of Object.values(ledger)) {
        if (value.pending && now() - value.time < DAYS && !queue.some(item => item.eventID === value.eventID)) queue.push(value.pending);
      }
    } catch { /* blocked storage: memory-only queue */ }
    captureAttribution(); ensureFrame();
    return () => { win.removeEventListener('message', message); frame?.remove(); frame = null; ready = false; lastPage = ''; };
  }
  return { enabled, track, page, captureAttribution, start };
}
